import { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import { QRCodeCanvas } from "qrcode.react";
import { v4 as uuidv4 } from "uuid";
import "../../styles/QRCodeModal.css";

/**
 * QRCodeModal Component
 * Generates a unique session ID and displays a QR code that users scan on mobile
 * @param {boolean} show - Whether modal is visible
 * @param {function} onHide - Callback when modal closes
 * @param {function} onSessionCreated - Callback with sessionId when QR is generated
 * @param {boolean} sessionExpired - Whether the session has expired
 * @param {function} onRegenerateSession - Callback when regenerating after expiry
 * @param {string} pin - 6-digit PIN for mobile verification
 */
const QRCodeModal = ({
  show,
  onHide,
  onSessionCreated,
  sessionExpired = false,
  onRegenerateSession,
  pin,
}) => {
  const [sessionId, setSessionId] = useState("");
  const [mobileUrl, setMobileUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  // Generate unique session ID once when component mounts
  useEffect(() => {
    const newSessionId = uuidv4();
    setSessionId(newSessionId);

    // Generate mobile upload URL
    const baseUrl = window.location.origin;
    const uploadUrl = `${baseUrl}/upload-mobile/${newSessionId}`;
    setMobileUrl(uploadUrl);

    // Notify parent component of new session
    if (onSessionCreated) {
      onSessionCreated(newSessionId);
    }
  }, []);

  /**
   * Copy mobile URL to clipboard
   */
  const copyToClipboard = async () => {
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(mobileUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        // Fallback for older browsers or non-secure contexts
        const textArea = document.createElement("textarea");
        textArea.value = mobileUrl;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          document.execCommand("copy");
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch (execErr) {
          console.error("Fallback copy failed:", execErr);
          setError("Unable to copy. Please copy manually: " + mobileUrl);
        }

        document.body.removeChild(textArea);
      }
    } catch (err) {
      console.error("Failed to copy:", err);
      setError("Unable to copy. Please copy manually: " + mobileUrl);
    }
  };

  /**
   * Download QR code as image
   */
  const downloadQRCode = () => {
    const qrElement = document.getElementById("qr-code");
    if (qrElement) {
      const canvas = qrElement.querySelector("canvas");
      const image = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = image;
      link.download = `qr-code-${sessionId}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  /**
   * Regenerate QR code after session expiry
   */
  const handleRegenerateQRCode = () => {
    const newSessionId = uuidv4();
    setSessionId(newSessionId);

    // Generate mobile upload URL
    const baseUrl = window.location.origin;
    const uploadUrl = `${baseUrl}/upload-mobile/${newSessionId}`;
    setMobileUrl(uploadUrl);

    // Notify parent component of new session
    if (onSessionCreated) {
      onSessionCreated(newSessionId);
    }

    if (onRegenerateSession) {
      onRegenerateSession();
    }

    setCopied(false);
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size='md'
      className='qr-code-modal'
    >
      <Modal.Header closeButton className='qr-modal-header'>
        <Modal.Title>Upload Photos from Your Phone</Modal.Title>
      </Modal.Header>

      <Modal.Body className='qr-modal-body'>
        {error && (
          <div className='alert alert-danger' style={{ marginBottom: "1rem" }}>
            {error}
          </div>
        )}
        {sessionExpired && (
          <div
            className='alert alert-warning '
            style={{ marginBottom: "1rem" }}
          >
            <strong>
              <i className='bi bi-exclamation-triangle-fill'></i> Session
              Expired
            </strong>
            <p style={{ marginBottom: "0.5rem", marginTop: "0.5rem" }}>
              This QR code has expired. Click the button below to generate a new
              QR code.
            </p>
            <Button
              className='mx-auto d-block'
              variant='primary'
              onClick={handleRegenerateQRCode}
              style={{ marginTop: "0.5rem" }}
            >
              <i className='bi bi-arrow-repeat'></i> Generate New QR Code
            </Button>
          </div>
        )}

        <div className='qr-instructions'>
          <h5>Step 1: Scan the QR Code</h5>
          <p>
            Use your phone camera or a QR code scanner app to scan the QR code
            below.
          </p>
        </div>

        <div className='qr-code-container' id='qr-code'>
          {sessionId && (
            <QRCodeCanvas
              value={mobileUrl}
              size={280}
              level='H'
              includeMargin={true}
              className='qr-code-image'
              fgColor='#000000'
              bgColor='#ffffff'
            />
          )}
        </div>

        {pin && (
          <div
            className='pin-display-container'
            style={{
              backgroundColor: "#f8f9fa",
              border: "2px solid #0d6efd",
              borderRadius: "8px",
              padding: "1rem",
              marginTop: "1rem",
              marginBottom: "1rem",
              textAlign: "center",
            }}
          >
            <p
              style={{
                fontSize: "0.9rem",
                fontWeight: "600",
                color: "#495057",
                marginBottom: "0.5rem",
              }}
            >
              <i className='bi bi-phone'></i> Mobile users must enter this PIN:
            </p>
            <div
              style={{
                fontSize: "2.5rem",
                fontWeight: "bold",
                color: "#0d6efd",
                letterSpacing: "0.5rem",
                fontFamily: "monospace",
              }}
            >
              {pin}
            </div>
            <p
              style={{
                fontSize: "0.85rem",
                color: "#6c757d",
                marginTop: "0.5rem",
                marginBottom: "0",
              }}
            >
              <i className='bi bi-info-circle'></i> Enter this PIN on your
              mobile device after scanning the QR code
            </p>
          </div>
        )}

        <div className='qr-session-info'>
          <p className='session-label'>Session ID:</p>
          <p className='session-id'>{sessionId}</p>
        </div>

        <div className='qr-url-section'>
          <label className='url-label'>Or open this link on your phone:</label>
          <div className='url-input-group'>
            <input
              type='text'
              className='url-input'
              value={mobileUrl}
              readOnly
            />
            <button
              className={`copy-button ${copied ? "copied" : ""}`}
              onClick={copyToClipboard}
            >
              {copied ? "✓ Copied" : "Copy"}
            </button>
          </div>
        </div>

        <div className='qr-instructions'>
          <h5>Step 2: Enter the PIN</h5>
          <p>
            After scanning the QR code, you'll need to enter the 6-digit PIN
            shown above to verify your device.
          </p>

          <h5>Step 3: Take or Upload Photos</h5>
          <p>
            Once verified, you can take photos directly from your camera or
            select them from your gallery.
          </p>

          <h5>Step 4: Wait for Confirmation</h5>
          <p>
            The photos will automatically appear on this desktop app once they
            are uploaded. You can upload up to 3 photos per sample.
          </p>
        </div>
      </Modal.Body>

      <Modal.Footer className='qr-modal-footer'>
        <Button
          variant='secondary'
          onClick={downloadQRCode}
          className='download-qr-btn'
        >
          <i className='bi bi-download'></i> Download QR Code
        </Button>
        <Button variant='primary' onClick={onHide} className='close-qr-btn'>
          <i className='bi bi-x-lg'></i> Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default QRCodeModal;
